import { baseApi as api } from './api.base.js';
const injectedRtkApi = api.injectEndpoints({
  endpoints: (build) => ({
    apiControllerGetTokenDetails: build.query<
      ApiControllerGetTokenDetailsApiResponse,
      ApiControllerGetTokenDetailsApiArg
    >({
      query: (queryArg) => ({
        url: `/web/token/info/${queryArg.blockchainId}/${queryArg.address}`,
      }),
    }),
    apiControllerFormatTokenAddress: build.query<
      ApiControllerFormatTokenAddressApiResponse,
      ApiControllerFormatTokenAddressApiArg
    >({
      query: (queryArg) => ({
        url: `/web/token/formataddress/${queryArg.blockchainId}/${queryArg.address}`,
      }),
    }),
    apiControllerGetPool: build.query<ApiControllerGetPoolApiResponse, ApiControllerGetPoolApiArg>({
      query: (queryArg) => ({
        url: `/web/getpool/${queryArg.exchangeId}`,
        params: {
          baseToken: queryArg.baseToken,
          quoteToken: queryArg.quoteToken,
          poolFee: queryArg.poolFee,
        },
      }),
    }),
    apiControllerGenerateAddress: build.query<
      ApiControllerGenerateAddressApiResponse,
      ApiControllerGenerateAddressApiArg
    >({
      query: (queryArg) => ({ url: `/web/generate/${queryArg.blockchainId}` }),
    }),
    apiControllerGetQuoteTokens: build.query<
      ApiControllerGetQuoteTokensApiResponse,
      ApiControllerGetQuoteTokensApiArg
    >({
      query: (queryArg) => ({
        url: `/web/quote_tokens/${queryArg.blockchainId}`,
      }),
    }),
  }),
  overrideExisting: false,
});
export { injectedRtkApi as generatedApi };
export type ApiControllerGetTokenDetailsApiResponse =
  /** status 200 Returns token details including name, symbol, decimals */ TokenInfoResponse;
export type ApiControllerGetTokenDetailsApiArg = {
  /** Blockchain ID */
  blockchainId: number;
  /** Base64 encoded token contract address */
  address: string;
};
export type ApiControllerFormatTokenAddressApiResponse =
  /** status 200 Returns formatted address string or validation result */ FormatAddressResponse;
export type ApiControllerFormatTokenAddressApiArg = {
  /** Blockchain ID */
  blockchainId: number;
  /** Base64 encoded address */
  address: string;
};
export type ApiControllerGetPoolApiResponse =
  /** status 200 Returns pool address/info */ PoolResponse;
export type ApiControllerGetPoolApiArg = {
  /** Exchange ID */
  exchangeId: number;
  /** Base token address */
  baseToken: string;
  /** Quote token address */
  quoteToken: string;
  /** Pool fee (number, fractional allowed) */
  poolFee?: number;
};
export type ApiControllerGenerateAddressApiResponse =
  /** status 200 Generated address payload (fields depend on blockchain type) */ GenerateAddressResponse;
export type ApiControllerGenerateAddressApiArg = {
  /** Blockchain ID */
  blockchainId: number;
};
export type ApiControllerGetQuoteTokensApiResponse =
  /** status 200 Returns quote tokens for the given blockchain */ QuoteTokensResponse;
export type ApiControllerGetQuoteTokensApiArg = {
  /** Blockchain ID */
  blockchainId: number;
};
export type TokenInfoData = {
  /** Token symbol */
  symbol: string;
  /** Token name */
  name?: string;
  /** Decimals */
  decimals: number;
  /** Token supply if returned by blockchain provider */
  supply?: object;
};
export type TokenInfoResponse = {
  /** Operation status */
  status: boolean;
  data: TokenInfoData;
};
export type FormatAddressResponse = {
  /** Operation status */
  status: boolean;
  /** Formatted address */
  data: string;
};
export type PoolResponse = {
  /** Operation status */
  status: boolean;
  /** Pool address for the given exchange and token pair. Returns null for aggregator exchanges (Odos, 1inch) and unsupported handlers. */
  data?: string | null;
};
export type GenerateAddressData = {
  /** Generated address (format depends on chain) */
  address: string;
  /** Generated private key. Encoding depends on chain: EVM/Bittensor — hex with 0x prefix; Cosmos — hex without prefix; Solana — base58; Sui — chain-native secret key string */
  privateKey: string;
  /** Public key. Returned for Bittensor (hex with 0x) and Sui (hex without prefix). Omitted for EVM, Cosmos and Solana. */
  publicKey?: string;
  /** BIP39 / Substrate mnemonic. Returned for Bittensor, Solana and Cosmos. Omitted for EVM and Sui. */
  mnemonic?: string;
};
export type GenerateAddressResponse = {
  /** Operation status */
  status: boolean;
  /** Generated keypair payload. Returns null for blockchains that do not support address generation (e.g. TON). */
  data: GenerateAddressData | null;
};
export type QuoteTokenItem = {
  symbol: string;
  address: string;
  decimals?: number;
};
export type QuoteTokensResponse = {
  /** Operation status */
  status: boolean;
  data: QuoteTokenItem[];
};
